import { useNavigate, Link } from 'react-router-dom';
import { Activity, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Table, Th, Td, Tr } from '@/components/ui/Table';
import { EmptyState, ErrorState } from '@/components/ui/States';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { runStateTone } from '@/components/runs/RunTranscript';
import { useAsync } from '@/lib/useAsync';
import { timeAgo } from '@/lib/timeAgo';
import type { RollupRun, RollupDeliverable, RollupProjectRef } from '@/lib/api';

/**
 * The one pair of top-down rollup tables. Company and workspace tabs differ
 * only in the fetcher they pass; every row carries its own workspace/project
 * refs, so link paths resolve the same way in both scopes.
 */

const projectBase = (companySlug: string, p: RollupProjectRef) =>
  `/c/${companySlug}/w/${p.workspace.slug}/p/${p.slug}`;

export function RunsRollup({
  fetchKey,
  fetch,
  companySlug,
}: {
  fetchKey: string;
  fetch: () => Promise<RollupRun[]>;
  companySlug: string;
}) {
  const runs = useAsync(() => fetch(), [fetchKey]);
  const navigate = useNavigate();

  if (runs.status === 'loading') return <SkeletonRows rows={5} />;
  if (runs.status === 'error') return <ErrorState error={runs.error} onRetry={runs.reload} />;
  if (runs.data.length === 0) {
    return <EmptyState icon={Activity} title="No runs yet" description="Agent activity lands here as projects run." />;
  }

  return (
    <Table>
      <thead className="bg-surface-2">
        <tr>
          <Th>Agent</Th>
          <Th>Location</Th>
          <Th>State</Th>
          <Th>When</Th>
        </tr>
      </thead>
      <tbody>
        {runs.data.map((r) => (
          <Tr
            key={r.id}
            onClick={() => navigate(`${projectBase(companySlug, r.project)}/runs/${r.id}`)}
            className="cursor-pointer hover:bg-surface-2/50"
          >
            <Td className="max-w-80">
              <span className="block truncate font-medium text-fg">{r.agent?.name ?? r.title ?? 'Run'}</span>
              {r.state === 'FAILED' && r.reason && (
                <span className="mt-0.5 block max-w-72 truncate text-xs text-danger" title={r.reason}>
                  {r.reason}
                </span>
              )}
            </Td>
            <Td className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
              <Link
                to={`/c/${companySlug}/w/${r.project.workspace.slug}`}
                className="text-fg-muted hover:text-primary"
              >
                {r.project.workspace.name}
              </Link>
              <span className="text-fg-subtle"> · </span>
              <Link to={projectBase(companySlug, r.project)} className="text-fg-muted hover:text-primary">
                {r.project.name}
              </Link>
            </Td>
            <Td className="whitespace-nowrap">
              <Badge tone={runStateTone(r.state)}>{r.state.toLowerCase()}</Badge>
              {r.scheduled && <span className="ml-1.5 text-xs text-fg-subtle">scheduled</span>}
            </Td>
            <Td className="whitespace-nowrap text-fg-muted">{timeAgo(r.createdAt)}</Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

export function DeliverablesRollup({
  fetchKey,
  fetch,
  companySlug,
}: {
  fetchKey: string;
  fetch: () => Promise<RollupDeliverable[]>;
  companySlug: string;
}) {
  const deliverables = useAsync(() => fetch(), [fetchKey]);
  const navigate = useNavigate();

  if (deliverables.status === 'loading') return <SkeletonRows rows={5} />;
  if (deliverables.status === 'error') return <ErrorState error={deliverables.error} onRetry={deliverables.reload} />;
  if (deliverables.data.length === 0) {
    return (
      <EmptyState icon={FileText} title="No deliverables yet" description="Reports appear here as runs produce them." />
    );
  }

  return (
    <Table>
      <thead className="bg-surface-2">
        <tr>
          <Th>Title</Th>
          <Th>Location</Th>
          <Th>Path</Th>
          <Th className="text-right">Version</Th>
          <Th>Visibility</Th>
          <Th>Updated</Th>
        </tr>
      </thead>
      <tbody>
        {deliverables.data.map((d) => (
          <Tr
            key={d.id}
            onClick={() => navigate(`${projectBase(companySlug, d.project)}/deliverables/${d.id}`)}
            className="cursor-pointer hover:bg-surface-2/50"
          >
            <Td className="max-w-80">
              <span className="block truncate font-medium text-fg">{d.title}</span>
            </Td>
            <Td className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
              <Link
                to={`/c/${companySlug}/w/${d.project.workspace.slug}`}
                className="text-fg-muted hover:text-primary"
              >
                {d.project.workspace.name}
              </Link>
              <span className="text-fg-subtle"> · </span>
              <Link to={projectBase(companySlug, d.project)} className="text-fg-muted hover:text-primary">
                {d.project.name}
              </Link>
            </Td>
            <Td className="max-w-64">
              <span className="block truncate text-xs text-fg-subtle" title={d.path}>
                {d.path}
              </span>
            </Td>
            <Td className="whitespace-nowrap text-right tabular-nums text-fg">v{d.version}</Td>
            <Td className="whitespace-nowrap">
              <Badge tone={d.visibility === 'CLIENT' ? 'primary' : 'neutral'}>{d.visibility.toLowerCase()}</Badge>
            </Td>
            <Td className="whitespace-nowrap text-fg-muted">{timeAgo(d.updatedAt)}</Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}
